// Backend
import {
  authenticateRequest,
  mfaChoicesRequest,
  postOTPAuthorizeRequest,
  verifyConsentRequest,
  acceptConsentRequest,
  verifyOTPRequest,
  logout,
} from '../../backend/authRequests';
import { getForwardedHost } from '../../backend';
import { getMFAOptionsFromCallbacks } from '../../lib/mfaHelpers';

// Helpers
import { to, checkRequestFailure } from '../../lib/requestHelpers';
import { getOrCreateDeviceFingerprint } from '../../lib/deviceFingerprint';
import {
  parseResponseUrlQuery,
  retrieveRequestId,
  clearAuthSession,
} from './authAuthorizeHelpers';

export const AUTH_UPDATE_STATE = '[AUTH] Update State';
export const AUTH_CLEAR_STATE = '[AUTH] Clear State';
export const AUTH_SET_USERNAME = '[AUTH] Set Username';
export const AUTH_SET_PASSWORD = '[AUTH] Set Password';
export const AUTH_SIGN_IN_REQUEST = '[AUTH] Sign In Request';
export const AUTH_SIGN_IN_SUCCESS = '[AUTH] Sign In Success';
export const AUTH_SIGN_IN_FAILED = '[AUTH] Sign In Failed';
export const AUTH_SET_STEP = '[AUTH] Set Step';
export const AUTH_SELECT_MFA = '[AUTH] Select MFA';
export const AUTH_SET_OTP = '[AUTH] Set OTP';
export const AUTH_VERIFY_OTP_REQUEST = '[AUTH] Verify OTP Request';
// Fired after auth + consent decisions are complete to finalize local auth state and optional next-step routing.
export const AUTH_POST_AUTH_SUCCESS = '[AUTH] Post Auth Success';
export const AUTH_VERIFY_OTP_FAILED = '[AUTH] Verify OTP Failed';

export const AUTH_ACCEPT_CONSENT_REQUEST = '[AUTH] Accept Consent Request';
export const AUTH_ACCEPT_CONSENT_SUCCESS = '[AUTH] Accept Consent Success';
export const AUTH_ACCEPT_CONSENT_FAILED = '[AUTH] Accept Consent Failed';
export const AUTH_GET_MFA_CHOICES_REQUEST = '[AUTH] Get MFA Choices Request';
export const AUTH_GET_MFA_CHOICES_SUCCESS = '[AUTH] Get MFA Choices Success';
export const AUTH_GET_MFA_CHOICES_FAILED  = '[AUTH] Get MFA Choices Failed';
export const AUTH_SIGN_IN_DIFFERENT = '[AUTH] Sign In Different';
export const AUTH_LOCKOUT_DETECTED = '[AUTH] Lockout Detected';
export const AUTH_CLOSE_LOCKOUT_MODAL = '[AUTH] Close Lockout Modal';
export const AUTH_SERVER_ERROR_MODAL_OPEN = '[AUTH] Server Error Modal Open';
export const AUTH_SERVER_ERROR_MODAL_CLOSE = '[AUTH] Server Error Modal Close';
export const AUTH_TIMEOUT_MODAL_OPEN = '[AUTH] Timeout Modal Open';
export const AUTH_TIMEOUT_MODAL_CLOSE = '[AUTH] Timeout Modal Close';
export const AUTH_SERVER_ERROR = '[AUTH] Server Error';
export const AUTH_SET_USER_DETAILS = '[AUTH] Set User Details';
export const AUTH_CLEAR_USER_DETAILS = '[AUTH] Clear User Details';

export const AUTH_STEPS = {
  SIGN_IN: 'sign-in',
  CHOOSE_MFA: 'choose-mfa',
  ENTER_OTP: 'enter-otp',
  GRANT_PERMISSION: 'grant-permission',
};

// 2 minutes in milliseconds
export const TIMEOUT_DURATION = 2 * 60 * 1000;

export const AUTH_SESSION_STEPS = {
  CONTINUE_AS: 'session-continue-as',
};

export const MFA_OPTIONS = {
  SMS: 'SMS',
  EMAIL: 'EMAIL',
};

export const startAuthorizeTimer = (dispatch) => {
  setTimeout(() => {
    dispatch(openTimeoutModal());
  }, TIMEOUT_DURATION);
};

const clonePayload = payload => JSON.parse(JSON.stringify(payload || {}));

const buildMFARequestPayload = (authenticatePayload, selectedMethod) => {
  const requestPayload = clonePayload(authenticatePayload);
  const callbacks = requestPayload.callbacks || [];
  const choiceCallback = callbacks.find(cb => cb?.type === 'ChoiceCallback');

  if (!choiceCallback) return requestPayload;

  const choicesOutput = choiceCallback.output?.find(out => out?.name === 'choices');
  const availableChoices = Array.isArray(choicesOutput?.value) ? choicesOutput.value : [];

  const selectedIndex = availableChoices.findIndex(choice => {
    const isEmail = /@/.test(choice);
    return (selectedMethod === MFA_OPTIONS.EMAIL && isEmail) || (selectedMethod === MFA_OPTIONS.SMS && !isEmail);
  });
  const nextIndex = selectedIndex >= 0 ? selectedIndex : 0;

  const idTokenInput = choiceCallback.input?.find(input => input?.name === 'IDToken2');
  if (idTokenInput) {
    idTokenInput.value = nextIndex;
  }

  // Add mfaChoice field
  requestPayload.mfaChoice = selectedMethod;

  return requestPayload;
};

const setCallbackInputValue = (callbacks, inputName, value) => {
  for (const callback of callbacks) {
    const callbackInput = callback?.input?.find(input => input?.name === inputName);
    if (callbackInput) {
      callbackInput.value = value;
      return true;
    }
  }

  return false;
};

const buildProcessOTPRequestPayload = (authenticatePayload, otp, rememberDevice, selectedMFA) => {
  const requestPayload = clonePayload(authenticatePayload);
  const callbacks = requestPayload?.callbacks || [];

  setCallbackInputValue(callbacks, 'IDToken1', otp);
  setCallbackInputValue(callbacks, 'IDToken0', rememberDevice ? 0 : 1);

  // Add mfaChoice field
  if (selectedMFA) {
    requestPayload.mfaChoice = selectedMFA;
  }

  return requestPayload;
};

const buildResendOTPRequestPayload = authenticatePayload => {
  const requestPayload = clonePayload(authenticatePayload);
  const callbacks = requestPayload?.callbacks || [];

  setCallbackInputValue(callbacks, 'IDToken1', '');
  setCallbackInputValue(callbacks, 'IDToken2', 1);

  return requestPayload;
};

const showServerErrorModal = () => (dispatch, getState) => {
  const uniqueIdentifier = retrieveRequestId() || getState()?.auth?.requestId || null;
  dispatch({ type: AUTH_SERVER_ERROR_MODAL_OPEN, payload: { uniqueIdentifier } });
};

const handleRedirect = (location, dispatch) => {
  if (location) {
    clearAuthSession();
    window.location.href = location;
    return true;
  } else {
    dispatch(showServerErrorModal());
    return false;
  }
};

const handleSamlRedirect = (dispatch, getState) => {
  const authState = getState()?.auth || {};
  const goToUrl = authState?.goToUrl || null;

  return handleRedirect(goToUrl, dispatch);
};

const handleAuthorizeAndConsent = async (dispatch, { clientId, agencyId, userAliasId, forwardedHost, customerAppId, authenticatePayload }) => {
  // Make authorize call
  const [authorizeResult, authorizeErr] = await to(dispatch(postOTPAuthorizeRequest({ 
    clientId, 
    agencyId,
    userAliasId,
    forwardedHost,
    customerAppId,
  })));

  const location = authorizeResult?.body?.location || null;
  const authorizeResponseParams = parseResponseUrlQuery(location);
  const authorizeCode = authorizeResponseParams.code;
  const customerAppClientId = clientId;

  const authorizeStatus = authorizeResult?.response?.status;
  if (authorizeErr || authorizeStatus !== 200 || !authorizeCode) {
    dispatch(showServerErrorModal());
    return { success: false };
  }

  // Start 2-minute timer if code is present
  startAuthorizeTimer(dispatch);

  // Verify consent
  const verifyConsentOutcome = await dispatch(verifyConsent({ customerAppClientId }));

  if (verifyConsentOutcome?.failed) {
    dispatch(showServerErrorModal());
    return { success: false };
  }

  if (!verifyConsentOutcome?.showGrantPermission) {
    // Consent already given — redirect directly
    handleRedirect(location, dispatch);
    return { success: true, redirected: true };
  }

  dispatch(updateAuthState({
    authorizeResponse: {
      location,
      ...authorizeResponseParams,
    },
  }));
  dispatch({ type: AUTH_SIGN_IN_SUCCESS, payload: authenticatePayload });
  dispatch({
    type: AUTH_POST_AUTH_SUCCESS,
    payload: authenticatePayload,
  });
  dispatch(setStep(AUTH_STEPS.GRANT_PERMISSION));
  return { success: true, showGrantPermission: true };
};

// Actions
export const updateAuthState = payload => ({ type: AUTH_UPDATE_STATE, payload });
export const clearAuthState = () => ({ type: AUTH_CLEAR_STATE });
export const setUsername = payload => ({ type: AUTH_SET_USERNAME, payload });
export const setPassword = payload => ({ type: AUTH_SET_PASSWORD, payload });
export const setStep = payload => ({ type: AUTH_SET_STEP, payload });
export const selectMFAMethod = payload => ({ type: AUTH_SELECT_MFA, payload });
export const setOTP = payload => ({ type: AUTH_SET_OTP, payload });
export const signInAsDifferent = () => ({ type: AUTH_SIGN_IN_DIFFERENT });
export const closeLockoutModal = () => ({ type: AUTH_CLOSE_LOCKOUT_MODAL });
export const closeServerErrorModal = () => ({ type: AUTH_SERVER_ERROR_MODAL_CLOSE });
export const openTimeoutModal = () => ({ type: AUTH_TIMEOUT_MODAL_OPEN });
export const closeTimeoutModal = () => ({ type: AUTH_TIMEOUT_MODAL_CLOSE });
export const setUserDetails = payload => ({ type: AUTH_SET_USER_DETAILS, payload });
export const clearUserDetails = () => ({ type: AUTH_CLEAR_USER_DETAILS });

// 1. SIGN IN - Authenticate user with username/password, get MFA choices
export const signIn = credentials => async (dispatch, getState) => {
  dispatch({ type: AUTH_SIGN_IN_REQUEST });

  const deviceFingerprint = await getOrCreateDeviceFingerprint();
  const username = credentials?.username;
  const password = credentials?.password;
  const { clientId = '', agencyId = '', customerAppId = '', authType } = getState()?.auth || {};
  const forwardedHost = getForwardedHost();

  const [result, err] = await to(dispatch(authenticateRequest({
    deviceFingerprint,
    username,
    password,
    clientId,
    agencyId,
    forwardedHost,
    customerAppId,
  })));

  if (err || checkRequestFailure(result)) {
    const errorDescription = (result?.body?.errorDescription || result?.body?.message || '').toLowerCase();
    switch (errorDescription) {
      case 'user is locked since otp was incorrect':
      case 'user is locked':
        dispatch({ type: AUTH_LOCKOUT_DETECTED });
        break;
      
      case 'user credentials are incorrect':
        dispatch({ type: AUTH_SIGN_IN_FAILED, payload: { fieldErrors: { password: 'signInInvalidCredentialsError' } } });
        break;
      
      case 'unauthorized':
      case 'login failure':
        dispatch({ type: AUTH_SIGN_IN_FAILED, payload: { fieldErrors: { email: 'signInInvalidCredentialsError' } } });
        break;
      
      default:
        dispatch(showServerErrorModal());
        break;
    }
    return;
  }

  const authenticatePayload = result?.body || {};

  // Extract userAliasId from authenticate response and store separately
  const userAliasId = authenticatePayload?.userAliasId ?? null;

  // Keep explicit continuation credentials for MFA/OTP API calls even if UI state changes.
  dispatch(updateAuthState({
    continuationUsername: username,
    continuationPassword: password,
    userAliasId,
  }));

  if (authenticatePayload?.tokenId) {
    if (authType === 'saml') {
      return handleSamlRedirect(dispatch, getState);
    }

    const customerAppId = getState()?.auth?.customerAppId || '';
    const result = await handleAuthorizeAndConsent(dispatch, { clientId, agencyId, userAliasId, forwardedHost, customerAppId, authenticatePayload });
    return result;
  }

  // Validate that response contains valid MFA choice callbacks
  const mfaOptions = getMFAOptionsFromCallbacks(authenticatePayload?.callbacks || []);
  if (!mfaOptions || mfaOptions.length === 0) {
    // No tokenId and no valid MFA choices - unexpected response
    dispatch(showServerErrorModal());
    return;
  }

  dispatch({ type: AUTH_SIGN_IN_SUCCESS, payload: authenticatePayload });
  dispatch({ type: AUTH_GET_MFA_CHOICES_SUCCESS, payload: mfaOptions });
  dispatch(setStep(AUTH_STEPS.CHOOSE_MFA));
};

// 2. SUBMIT MFA CHOICE - User selects SMS or Email for OTP delivery
export const submitMFAChoice = selectedMethod => async (dispatch, getState) => {
  dispatch({ type: AUTH_GET_MFA_CHOICES_REQUEST });

  const { authenticatePayload, clientId = '', agencyId = '', customerAppId = '', userAliasId = '' } = getState()?.auth || {};
  const forwardedHost = getForwardedHost();
  const requestBody = buildMFARequestPayload(authenticatePayload, selectedMethod);

  const [result, err] = await to(dispatch(mfaChoicesRequest({
    body: requestBody,
    clientId,
    agencyId,
    userAliasId,
    forwardedHost,
    customerAppId,
  })));

  if (err || checkRequestFailure(result)) {
    dispatch(updateAuthState({ isLoading: false }));
    dispatch(showServerErrorModal());
    return;
  }

  const responseBody = result.body;
  dispatch(updateAuthState({ 
    authenticatePayload: responseBody,
    isLoading: false,
  }));
  dispatch(setStep(AUTH_STEPS.ENTER_OTP));
};

// 3. VERIFY OTP - Validate the one-time password, call authorize, then verify consent
export const verifyOTP = (otp, rememberDevice) => async (dispatch, getState) => {
  dispatch({ type: AUTH_VERIFY_OTP_REQUEST });

  if (!/^\d{6}$/.test(otp)) {
    dispatch({ type: AUTH_VERIFY_OTP_FAILED, payload: { fieldErrors: { otp: 'otpInvalidFormatError' } } });
    return;
  }

  const { authenticatePayload, selectedMFA, clientId = '', agencyId = '', customerAppId = '', userAliasId = '', authType } = getState()?.auth || {};
  const forwardedHost = getForwardedHost();

  const requestBody = buildProcessOTPRequestPayload(authenticatePayload, otp, rememberDevice, selectedMFA);

  const [result, err] = await to(dispatch(verifyOTPRequest({
    body: requestBody,
    clientId,
    agencyId,
    userAliasId,
    forwardedHost,
    customerAppId,
  })));

  if (err || checkRequestFailure(result)) {
    const errorDescription = (result?.body?.errorDescription || '').toLowerCase();
    
    switch (errorDescription) {
      case 'user is locked since otp was incorrect':
      case 'unauthorized':
        dispatch({ type: AUTH_LOCKOUT_DETECTED });
        break;
      
      default:
        dispatch(showServerErrorModal());
        break;
    }
    return;
  }

  const responseBody = result?.body || {};

  // If we got callbacks back, the OTP was rejected and user needs to try again
  if (responseBody?.callbacks && Array.isArray(responseBody.callbacks) && !responseBody?.tokenId) {
    dispatch({
      type: AUTH_VERIFY_OTP_FAILED,
      payload: { fieldErrors: { otp: 'otpInvalidCodeError' } },
    });
    // Update the authenticatePayload so the next attempt can use the new authId
    const updatedPayload = {
      ...authenticatePayload,
      authId: responseBody.authId || authenticatePayload.authId,
      callbacks: responseBody.callbacks,
    };
    dispatch(updateAuthState({ 
      authenticatePayload: updatedPayload,
    }));
    return;
  }

  // If no tokenId at this point, it's an unexpected response structure
  if (!responseBody?.tokenId) {
    dispatch(showServerErrorModal());
    return;
  }

  if (authType === 'saml') {
    return handleSamlRedirect(dispatch, getState);
  }

  const authorizeResult = await handleAuthorizeAndConsent(dispatch, { 
    clientId, 
    agencyId, 
    userAliasId, 
    forwardedHost: getForwardedHost(),
    customerAppId,
    authenticatePayload: responseBody 
  });
  
  // Return true if successful (component will handle navigation to grant permission page)
  return authorizeResult?.success || false;
};

// 4. RESEND OTP - Request a new OTP code (can be called during OTP entry)
export const resendOTP = () => async (dispatch, getState) => {
  const { authenticatePayload, clientId = '', agencyId = '', customerAppId = '', userAliasId = '' } = getState()?.auth || {};
  const forwardedHost = getForwardedHost();

  const requestBody = buildResendOTPRequestPayload(authenticatePayload);

  const [result, err] = await to(dispatch(verifyOTPRequest({
    body: requestBody,
    clientId,
    agencyId,
    userAliasId,
    forwardedHost,
    customerAppId,
  })));

  if (err || checkRequestFailure(result)) {
    const errorDescription = (result?.body?.errorDescription || '').toLowerCase();
    const errorMessage = (result?.body?.message || '').toLowerCase();
    
    // Handle specific resend limit errors
    if (errorDescription === 'resend limit has been reached' || 
        errorMessage.includes('resend limit') || 
        errorMessage.includes('too many')) {
      dispatch(updateAuthState({
        error: 'otpResendLimitSignInBannerError',
        username: '',
        password: '',
        otp: '',
        selectedMFA: null,
        authenticatePayload: null,
        mfaOptions: [],
      }));
      dispatch({ type: AUTH_SET_STEP, payload: AUTH_STEPS.SIGN_IN });
      return false;
    }
    dispatch(showServerErrorModal());
    return false;
  }

  const responseBody = result?.body || {};
  
  // Response should have authId for the next OTP attempt
  if (!responseBody?.authId) {
    dispatch(showServerErrorModal());
    return false;
  }

  dispatch(updateAuthState({
    authenticatePayload: responseBody,
    error: null,
  }));

  return true;
};

// 5. VERIFY CONSENT - Check if user has already granted permission for this app
export const verifyConsent = ({ customerAppClientId } = {}) => async (dispatch, getState) => {
  const { userAliasId = '', agencyId = '', customerAppId = '', requestId } = getState()?.auth || {};
  const resolvedCustomerAppClientId = customerAppClientId;
  const forwardedHost = getForwardedHost();
  // Fallback to Redux state if sessionStorage is cleared
  const uniqueIdentifier = retrieveRequestId() || requestId || null;
  
  // Call API to check if consent record exists for this user and app
  const [result, err] = await to(dispatch(verifyConsentRequest({ 
    customerAppClientId: resolvedCustomerAppClientId,
    agencyId,
    userAliasId,
    uniqueIdentifier,
    forwardedHost,
    customerAppId,
  })));

  const statusCode = result?.response?.status;
  
  // Handle 200 - consent record exists, must have accept: "Y" to redirect
  if (statusCode === 200) {
    if (checkRequestFailure(result)) {
      // Body has errors even though status is 200
      return {
        failed: true,
        uniqueIdentifier,
      };
    }
    
    // Valid consent with accept: "Y" - redirect
    return {
      failed: false,
      showGrantPermission: false,
      userAliasId: result?.body?.userAliasId || null,
    };
  }

  if (statusCode === 404) {
    return {
      failed: false,
      showGrantPermission: true,
      userAliasId: result?.body?.userAliasId || null,
    };
  }
  
  return {
    failed: true,
    uniqueIdentifier,
  };
};

// 6. ACCEPT CONSENT - User grants permission to the customer application
export const acceptConsent = () => async (dispatch, getState) => {
  dispatch({ type: AUTH_ACCEPT_CONSENT_REQUEST });

  const { userAliasId, clientId, agencyId = '', customerAppId = '', authorizeResponse } = getState()?.auth || {};
  const customerAppClientId = clientId;
  const uniqueIdentifier = retrieveRequestId();
  const forwardedHost = getForwardedHost();

  const [result, err] = await to(dispatch(acceptConsentRequest({ 
    userAliasId, 
    customerAppClientId,
    agencyId,
    uniqueIdentifier,
    forwardedHost,
    customerAppId,
  })));

  if (err || checkRequestFailure(result)) {
    dispatch({ type: AUTH_ACCEPT_CONSENT_FAILED });
    dispatch(showServerErrorModal());
    return false;
  }

  dispatch({ type: AUTH_ACCEPT_CONSENT_SUCCESS, payload: result?.body || {} });

  // Redirect to customer app using location from authorize response
  const location = authorizeResponse?.location;
  return handleRedirect(location, dispatch);
};

// 7. LOGOUT - Clear session and sign in as different user
export const logoutAndSignInDifferent = () => async (dispatch, getState) => {
  const { clientId = '', agencyId = '', customerAppId = '', userAliasId = '' } = getState()?.auth || {};
  const forwardedHost = getForwardedHost();

  const [result, err] = await to(dispatch(logout({
    clientId,
    agencyId,
    userAliasId,
    forwardedHost,
    customerAppId,
  })));

  const statusCode = result?.response?.status;

  if (err || statusCode !== 200) {
    dispatch(showServerErrorModal());
    return { success: false };
  }
  
  dispatch(signInAsDifferent());
  return { success: true };
};
